import React from 'react';
import { useNavigate } from 'react-router-dom';
import { navigateSafely, runSafely } from '../../../utils/navigation';
import { useTranslation } from 'react-i18next';
import { MultiMailArchiveDetails } from './MultiMailArchiveDetails';
import { openLink } from '../../../services/api/dialogService';
import { isMultipleMailArchiveEnabled } from '../../../services/api/authService';
import {
    MultiMailArchiveAllArchivedView,
    MultiMailArchiveDisabledView,
    MultiMailArchiveLoadingView,
    MultiMailArchiveReadyView,
    MultiMailArchiveSkippedBar,
} from './MultiMailArchiveHome.views';
import {
    computeRemainingItemIds,
    getMultipleArchivedMailInfoAsync,
    hasSelectedItemsChangedSupport,
    removeSelectedItemsChangedHandler,
    getArchivedItemIds,
    getArchivedDocuments,
    areAllSelectedMailsArchived,
    resolveSelectedMessageIdsForUi,
} from './MultiMailArchiveHome.utils';
import { useStyles } from './MultiMailArchiveHome.styles';
import { IDocumentDetails } from '../../../@types/models';

type ArchiveMode = 'remaining' | 'all';

export const MultiMailArchiveHome = () => {
    const styles = useStyles();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const isMultiSavedMode = new URLSearchParams(globalThis.location.search).get('multiSaved') === 'true';
    const isMultipleSaveEnabled = isMultipleMailArchiveEnabled();
    const cannotRetrieveMailIdText = t('Cannot retrieve mail ID for one or more selected emails');
    const [isLoadingArchivedInfo, setIsLoadingArchivedInfo] = React.useState(true);
    const [archivedDocuments, setArchivedDocuments] = React.useState<IDocumentDetails[]>([]);
    const [archivedItemIds, setArchivedItemIds] = React.useState<string[]>([]);
    const [selectedItemIdsSnapshot, setSelectedItemIdsSnapshot] = React.useState<string[]>([]);
    const [allMailsArchived, setAllMailsArchived] = React.useState(false);
    const [skippedMailsMessage, setSkippedMailsMessage] = React.useState<string | null>(null);
    const [archiveMode, setArchiveMode] = React.useState<ArchiveMode>('remaining');
    const archiveAllOptionText = t('All selected emails');
    const archiveChoiceText = {
        remainingOptionText: t('Only unarchived emails'),
        allOptionText: archiveAllOptionText,
        actionButtonText: t('Archive emails')
    };

    React.useEffect(() => {
        if (!isMultipleSaveEnabled) {
            setIsLoadingArchivedInfo(false);
            return;
        }

        let isDisposed = false;

        // Always re-check the actual archived state, even after returning from the archive dialog,
        // so the view reflects whether the user saved or cancelled instead of a static message.
        const loadArchivedInfo = async () => {
            setSkippedMailsMessage(null);
            setSelectedItemIdsSnapshot([]);
            setArchivedItemIds([]);
            setArchivedDocuments([]);
            setAllMailsArchived(false);
            setArchiveMode('remaining');

            try {
                const { itemIds: selectedItemIds, skippedCount } = await resolveSelectedMessageIdsForUi();
                if (skippedCount > 0) {
                    setSkippedMailsMessage(cannotRetrieveMailIdText);
                }
                if (isDisposed) return;

                const resData = await getMultipleArchivedMailInfoAsync();
                if (isDisposed) return;

                const responseItemIds = Object.keys(resData);
                const effectiveSelectedItemIds = responseItemIds.length > 0
                    ? responseItemIds
                    : selectedItemIds;
                const nextArchivedItemIds = getArchivedItemIds(resData);
                const nextArchivedDocuments = getArchivedDocuments(resData);
                const isAllSelectedArchived = areAllSelectedMailsArchived(effectiveSelectedItemIds, nextArchivedItemIds);

                setSelectedItemIdsSnapshot(Array.from(new Set(effectiveSelectedItemIds)));
                setAllMailsArchived(isAllSelectedArchived);
                setArchivedItemIds(nextArchivedItemIds);
                setArchivedDocuments(nextArchivedDocuments);
                setIsLoadingArchivedInfo(false);
            } catch {
                if (!isDisposed) setIsLoadingArchivedInfo(false);
            }
        };

        const onSelectionsChanged = () => {
            // Drop the multiSaved marker once the selection changes so a fresh load isn't mistaken for the dialog result.
            if (isMultiSavedMode) {
                navigateSafely(navigate, '/archivehome');
                return;
            }
            setIsLoadingArchivedInfo(true);
            void loadArchivedInfo();
        };

        setIsLoadingArchivedInfo(true);
        void loadArchivedInfo();
        const supportsSelectedItemsChanged = hasSelectedItemsChangedSupport();

        if (supportsSelectedItemsChanged) {
            Office.context.mailbox.addHandlerAsync(Office.EventType.SelectedItemsChanged, onSelectionsChanged);
            return () => {
                removeSelectedItemsChangedHandler(onSelectionsChanged);
                isDisposed = true;
            };
        }

        return () => {
            isDisposed = true;
        };
    }, [cannotRetrieveMailIdText, isMultipleSaveEnabled, isMultiSavedMode, navigate]);

    const getSelectedItemIdsToNavigate = React.useCallback(async () => {
        if (hasSelectedItemsChangedSupport() && selectedItemIdsSnapshot.length > 0) return selectedItemIdsSnapshot;

        const { itemIds: selectedItemIds, skippedCount } = await resolveSelectedMessageIdsForUi();
        if (skippedCount > 0) setSkippedMailsMessage(cannotRetrieveMailIdText);

        return Array.from(new Set(selectedItemIds));
    }, [cannotRetrieveMailIdText, selectedItemIdsSnapshot]);

    const navigateToArchiveMultipleMail = React.useCallback(async () => {
        setSkippedMailsMessage(null);
        const selectedItemIds = await getSelectedItemIdsToNavigate();

        if (archiveMode === 'all') {
            if (selectedItemIds.length === 0) return;
            navigateSafely(navigate, '/archiveMultipleMail', { state: { itemIds: selectedItemIds } });
            return;
        }

        const itemIdsToNavigate = computeRemainingItemIds(selectedItemIds, archivedItemIds);
        if (itemIdsToNavigate === null) {
            setAllMailsArchived(true);
            return;
        }
        navigateSafely(navigate, '/archiveMultipleMail', { state: { itemIds: itemIdsToNavigate } });
    }, [archiveMode, archivedItemIds, getSelectedItemIdsToNavigate, navigate, setSkippedMailsMessage]);

    const navigateToArchiveAllSelectedMails = React.useCallback(async () => {
        setSkippedMailsMessage(null);
        const selectedItemIds = await getSelectedItemIdsToNavigate();
        if (selectedItemIds.length === 0) return;
        navigateSafely(navigate, '/archiveMultipleMail', { state: { itemIds: selectedItemIds } });
    }, [getSelectedItemIdsToNavigate, navigate]);

    if (!isMultipleSaveEnabled) {
        return <MultiMailArchiveDisabledView classNames={styles} text={{ title: t('Multiple email archive is not enabled') }} />;
    }

    if (isLoadingArchivedInfo) {
        return <MultiMailArchiveLoadingView classNames={styles} title={t('Archive mails')} loadingLabel={t('Loading...')} />;
    }

    const hasArchivedDocuments = archivedDocuments.length > 0;
    // Only offer the shortcut to already-archived documents once the dialog round-trip actually produced results.
    const onShowDocuments = hasArchivedDocuments
        ? () => openLink('GlobalDesktop/Standard/UnregisteredDocuments')
        : undefined;
    const archivedInfoContent = hasArchivedDocuments
        ? <MultiMailArchiveDetails documents={archivedDocuments} onOpenUnregisteredDesktop={onShowDocuments} />
        : null;
    const skippedMailsBar = <MultiMailArchiveSkippedBar message={skippedMailsMessage} />;

    if (allMailsArchived) {
        return (
            <MultiMailArchiveAllArchivedView
                classNames={styles}
                text={{
                    title: t('Archive mails'),
                    summary: t('All selected mails are already archived.'),
                    description: t('You can view the already archived documents in 360°.'),
                    archiveAllButtonText: t('Archive all emails anyway'),
                }}
                skippedMailsBar={skippedMailsBar}
                archivedInfoContent={archivedInfoContent}
                onArchiveMails={() => runSafely(navigateToArchiveAllSelectedMails)}
            />
        );
    }
    const headerDescription = hasArchivedDocuments ? t('Some of these emails have already been archived.') : t('These mails are not saved in 360°.');
    const cardDescription = hasArchivedDocuments ? t('What do you want to archive?') : t('These can be saved in one go as separate unregistered documents.');
    const noteText = hasArchivedDocuments
        ? t('Note. Emails will be archived as individual unregistered documents in one go.')
        : undefined;

    return (
        <MultiMailArchiveReadyView
            classNames={styles}
            text={{
                title: t('Archive mails'),
                headerDescription,
                cardDescription,
                noteText,
                archiveChoice: hasArchivedDocuments
                    ? archiveChoiceText
                    : { remainingOptionText: t('Archive selected mails'), allOptionText: archiveAllOptionText, actionButtonText: t('Archive emails') },
            }}
            showArchiveModeChoice={hasArchivedDocuments}
            selectedArchiveMode={archiveMode}
            onArchiveModeChange={(value) => setArchiveMode(value as ArchiveMode)}
            skippedMailsBar={skippedMailsBar}
            archivedInfoContent={archivedInfoContent}
            onArchiveMails={() => runSafely(navigateToArchiveMultipleMail)}
        />
    );
};
